import React, { useState } from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SettingsIcon from "@mui/icons-material/Settings";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import TableRowsIcon from "@mui/icons-material/TableRows";
import { useTranslation } from "react-i18next";
import {
  useApplicationPackages,
  ApplicationPackage,
  ApplicationPackageRequest,
} from "../../../hooks/useApplicationPackages";
import { useError } from "../../../contexts/ErrorContext";
import EnhancedTable from "../../../components/common/EnhancedTable";
import ActionButtons from "../../../components/common/ActionButtons";
import PackageFormModal from "../../../components/Modals/PackageFormModal";
import PackageServicesModal from "../../../components/Modals/PackageServicesModal";
import PackageCardsView from "../../../components/admin/PackageCardsView";

const PackageManagement: React.FC = () => {
  const { t } = useTranslation();
  const { showError, showSuccess } = useError();
  const { packagesQuery, createPackageMutation, updatePackageMutation, deletePackageMutation } =
    useApplicationPackages();

  const [modalOpen, setModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [currentPackage, setCurrentPackage] = useState<ApplicationPackage | undefined>(undefined);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [packageToDelete, setPackageToDelete] = useState<number | null>(null);
  const [servicesModalOpen, setServicesModalOpen] = useState(false);
  const [packageForServices, setPackageForServices] = useState<ApplicationPackage | null>(null);
  const [viewMode, setViewMode] = useState<"table" | "cards">("table");

  const handleOpenAddModal = () => {
    setIsEdit(false);
    setCurrentPackage(undefined);
    setModalOpen(true);
  };

  const handleOpenEditModal = (pkg: ApplicationPackage) => {
    setIsEdit(true);
    setCurrentPackage(pkg);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setCurrentPackage(undefined);
  };

  const handleOpenServicesModal = (pkg: ApplicationPackage) => {
    setPackageForServices(pkg);
    setServicesModalOpen(true);
  };

  const handleSubmit = (formData: ApplicationPackageRequest & { id?: number }) => {
    const requestBody: ApplicationPackageRequest = {
      name: formData.name,
      description: formData.description,
      active: formData.active,
      price: formData.price,
    };

    if (isEdit && formData.id != null) {
      updatePackageMutation.mutate(
        { id: formData.id, data: requestBody },
        {
          onSuccess: () => {
            showSuccess(t("package_updated_successfully"));
            handleCloseModal();
          },
          onError: (error: unknown) => {
            const err = error as { response?: { data?: { message?: string } }; message?: string };
            showError(err?.response?.data?.message || err?.message || t("error_updating_package"));
          },
        }
      );
    } else {
      createPackageMutation.mutate(requestBody, {
        onSuccess: () => {
          showSuccess(t("package_added_successfully"));
          handleCloseModal();
        },
        onError: (error: unknown) => {
          const err = error as { response?: { data?: { message?: string } }; message?: string };
          showError(err?.response?.data?.message || err?.message || t("error_adding_package"));
        },
      });
    }
  };

  const handleConfirmDelete = () => {
    if (packageToDelete == null) return;
    deletePackageMutation.mutate(packageToDelete, {
      onSuccess: () => {
        showSuccess(t("package_deleted_successfully"));
        setDeleteDialogOpen(false);
        setPackageToDelete(null);
      },
      onError: (error: unknown) => {
        const err = error as { response?: { data?: { message?: string } }; message?: string };
        showError(err?.response?.data?.message || err?.message || t("error_deleting_package"));
      },
    });
  };

  const columns = [
    { id: "name", label: t("package_name"), minWidth: 160 },
    { id: "description", label: t("description"), minWidth: 200 },
    {
      id: "price",
      label: t("price"),
      minWidth: 100,
      align: "right" as const,
      format: (value: number) => (value != null ? `₹${Number(value).toFixed(2)}` : "-"),
    },
    {
      id: "active",
      label: t("status"),
      minWidth: 100,
      align: "center" as const,
      format: (value: boolean) => (
        <Chip
          label={value ? t("active") : t("inactive")}
          color={value ? "success" : "default"}
          size="small"
        />
      ),
    },
  ];

  const formattedData = (packagesQuery.data || []).map((row) => ({
    ...row,
    name: row.name || "-",
    description: row.description || "-",
    price: row.price ?? 0,
    active: row.active ?? false,
  }));

  const isSubmitting = createPackageMutation.isLoading || updatePackageMutation.isLoading;

  return (
    <>
      <Paper sx={{ p: 2, maxWidth: 1500, mx: "auto", mt: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography variant="h5" fontWeight="bold">
            {t("package_management")}
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={viewMode === "table" ? <ViewModuleIcon /> : <TableRowsIcon />}
              onClick={() => setViewMode((prev) => (prev === "table" ? "cards" : "table"))}
              sx={{ borderColor: "#3092e3", color: "#3092e3" }}
            >
              {viewMode === "table" ? t("view_packages") : t("table_view")}
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" } }}
              onClick={handleOpenAddModal}
            >
              {t("add_package")}
            </Button>
          </Box>
        </Box>

        {packagesQuery.isLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : packagesQuery.error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {packagesQuery.error.message || t("error_loading_packages")}
          </Alert>
        ) : viewMode === "cards" ? (
          <PackageCardsView packages={packagesQuery.data || []} />
        ) : (
          <EnhancedTable
            columns={columns}
            data={formattedData}
            searchFields={["name", "description"]}
            filterFields={[]}
            actions={(row: ApplicationPackage) => (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "100%",
                  gap: 0,
                }}
              >
                <Tooltip title={t("manage_package_services")}>
                  <IconButton size="small" onClick={() => handleOpenServicesModal(row)} sx={{ color: "primary.main" }}>
                    <SettingsIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <ActionButtons
                  onEdit={() => handleOpenEditModal(row)}
                  onDelete={() => row.id != null && (setPackageToDelete(row.id), setDeleteDialogOpen(true))}
                  editTooltip={t("edit")}
                  deleteTooltip={t("delete")}
                />
              </Box>
            )}
            cardProps={{
              elevation: 0,
              sx: { border: "none", boxShadow: "none", backgroundColor: "transparent" },
            }}
          />
        )}
      </Paper>

      <PackageFormModal
        open={modalOpen}
        handleClose={handleCloseModal}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        isEdit={isEdit}
        initialData={currentPackage}
      />

      <PackageServicesModal
        open={servicesModalOpen}
        handleClose={() => {
          setServicesModalOpen(false);
          setPackageForServices(null);
        }}
        packageData={packageForServices}
        onSuccess={() => showSuccess(t("package_services_saved_successfully"))}
        onError={(msg) => showError(msg)}
      />

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t("confirm_delete")}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t("confirm_delete_package")}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>{t("cancel")}</Button>
          <Button onClick={handleConfirmDelete} color="error" disabled={deletePackageMutation.isLoading}>
            {t("delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PackageManagement;
